import {type FC, useEffect, useRef, useState} from 'react';
import Hls from 'hls.js';
import {timeStringToSeconds} from '../../../utils/DateUtil.ts';
import {BackgroundVideo} from '../types/Streaming.styled.ts';
import useGlobalStore from '../../../store/useGlobalStore.ts';
import useStreamingStore from '../../../store/useStreamingStore.ts';
import useCenterStore from '../../../store/useCenterStore.ts';

interface IVodPlayer {
    videoUrl: string;
    startTime: string;
    endTime: string;
}

const VodPlayer: FC<IVodPlayer> = ({videoUrl, startTime, endTime}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [videoDuration, setVideoDuration] = useState<number>(-1);
    const isHLS = videoUrl?.endsWith('.m3u8') ?? false;
    const {streamingPollingData} = useStreamingStore();
    const {isFullScreen} = useGlobalStore();

    useEffect(() => {
        if (!videoDuration) return;
        const now = new Date(new Date().toLocaleString('en-US', {timeZone: 'Asia/Seoul'}));
        const currentSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
        const startPlaybackTime = timeStringToSeconds(startTime);
        const endPlaybackTime = timeStringToSeconds(endTime);
        if (currentSeconds >= startPlaybackTime && currentSeconds < endPlaybackTime) {
            const elapsedTime = currentSeconds - startPlaybackTime; // 시작부터 경과된 시간
            const repeatedVideoTime = elapsedTime % videoDuration; // 반복되는 비디오의 현재 재생 시간
            if (!isNaN(repeatedVideoTime) && repeatedVideoTime >= 0) {
                if (videoRef.current) {
                    videoRef.current.currentTime = repeatedVideoTime;
                }
            }
        }
    }, [videoDuration, isHLS]);

    useEffect(() => {
        if (!videoUrl || !videoRef.current) return;
        if (isHLS && Hls.isSupported()) {
            const hls = new Hls();
            hls.loadSource(videoUrl);
            hls.attachMedia(videoRef.current);
            return () => hls.destroy();
        }
        // mp4(및 네이티브 HLS)는 hls.js 없이 src로 직접 재생한다 — 이 분기가 없으면 검은 화면.
        videoRef.current.src = videoUrl;
    }, [videoUrl, isHLS]);

    return (
        <BackgroundVideo
            $isFullScreen={isFullScreen}
            $isBroadCasting={streamingPollingData && streamingPollingData.broadcast.content_type}
        >
            <video
                ref={videoRef}
                controls={false}
                autoPlay
                loop={true}
                width="100%"
                onLoadedMetadata={() => {
                    if (videoRef.current) {
                        setVideoDuration(Math.floor(videoRef.current.duration)); // HLS 비디오 길이 저장
                    }
                }}
            />
        </BackgroundVideo>
    );
};

export default VodPlayer;
